import React, { useState } from 'react';
import { Form, Input, Modal } from 'antd';
import callApi from '../Api/config';
import { toastError, toastSuccess } from '../service/Toast';
import { REGEX_PASSWORD } from '../utils/Regexp/Regex';
import { InvertFillButton } from './InvertFillButton';

const PASSWORD_RULE_MESSAGE =
  'Use at least 8 characters with an uppercase letter, a lowercase letter, a number and a symbol (@$!%*?&#^()).';

interface AdminPasswordModalProps {
  open: boolean;
  userId: string;
  userName?: string;
  onClose: () => void;
}

const toMessage = (message: unknown, fallback: string): string => {
  if (Array.isArray(message)) return message.filter(Boolean).join(', ') || fallback;
  if (typeof message === 'string' && message.trim()) return message;

  return fallback;
};

/** Super Admin sets a new password for another admin user (no current password needed). */
const AdminPasswordModal: React.FC<AdminPasswordModalProps> = ({
  open,
  userId,
  userName,
  onClose,
}) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const close = () => {
    form.resetFields();
    onClose();
  };

  const handleSubmit = async () => {
    let values: { newPassword: string };
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    setSaving(true);
    const response: any = await callApi(`/admin/users/${userId}/password`, 'PATCH', {
      newPassword: values.newPassword,
    });
    setSaving(false);

    if (response?.status && response?.data?.isSuccess) {
      toastSuccess(toMessage(response.data.message, 'Password updated successfully.'));
      close();
      return;
    }
    toastError(
      toMessage(
        response?.data?.message,
        'Could not update the password. Please check your connection and try again.'
      )
    );
  };

  return (
    <Modal
      open={open}
      title={`Change password${userName ? ` - ${userName}` : ''}`}
      onCancel={close}
      destroyOnClose
      footer={[
        <InvertFillButton key="cancel" variant="white" size="md" onClick={close} disabled={saving}>
          Cancel
        </InvertFillButton>,
        <InvertFillButton
          key="save"
          variant="blue"
          size="md"
          onClick={handleSubmit}
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save password'}
        </InvertFillButton>,
      ]}
    >
      <p style={{ marginBottom: 16, color: '#53657f' }}>
        The user can sign in with this password straight away and will get an email saying their
        password was changed.
      </p>
      <Form form={form} layout="vertical" requiredMark>
        <Form.Item
          name="newPassword"
          label="New password"
          rules={[
            { required: true, message: 'Please enter a new password.' },
            { pattern: REGEX_PASSWORD, message: PASSWORD_RULE_MESSAGE },
          ]}
        >
          <Input.Password placeholder="New password" autoComplete="new-password" />
        </Form.Item>
        <Form.Item
          name="confirmPassword"
          label="Confirm new password"
          dependencies={['newPassword']}
          rules={[
            { required: true, message: 'Please confirm the new password.' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value) return Promise.resolve();

                return Promise.reject(new Error('Passwords do not match.'));
              },
            }),
          ]}
        >
          <Input.Password placeholder="Confirm new password" autoComplete="new-password" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AdminPasswordModal;
